import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import { formatGeneralAddress } from '@components/BookingDetails/utils';
import { StepContainer } from '@components/Steps/Steps.styles';
import {
  LuxButton,
  LuxSelectablePill,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import useCustomTranslation from '@hooks/useCustomTranslation';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { TabletBreakpoint } from '@utils/consts/breakpoints.consts';
import { DirectionsCardMap } from './DirectionsCardMap';
import {
  buildDayRanges,
  DAY_ORDER,
  getCurrentClosingTime,
  getTodayOpeningHours,
  isOpenNow,
} from './utils';
import { useMemo, useState } from 'react';
import { DirectionsModal } from './DirectionsModal';

export function DirectionsCard() {
  const { t } = useCustomTranslation();
  const theme = useTheme();
  const practiceInfo = useOnlineBookingStore(state => state.generalInfo);
  const practiceConfig = useOnlineBookingStore(state => state.generalConfig);
  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);
  const [openDirectionsModal, setOpenDirectionsModal] =
    useState<boolean>(false);

  console.log('practiceInfo Hours:', practiceInfo?.hours);

  const daysOpeningHours = useMemo(() => {
    if (practiceInfo?.hours) {
      const dayToRangesStr = buildDayRanges(practiceInfo?.hours, t);

      return DAY_ORDER.map(day => ({
        key: day,
        label: t(`practiceCard.days.${day}`),
        ranges: dayToRangesStr[day],
      }));
    }
    return [];
  }, [practiceInfo?.hours, t]);

  const isOpen = useMemo(
    () => (practiceInfo?.hours ? isOpenNow(practiceInfo.hours) : false),
    [practiceInfo?.hours],
  );

  const closingTime = useMemo(
    () =>
      practiceInfo?.hours ? getCurrentClosingTime(practiceInfo.hours) : null,
    [practiceInfo?.hours],
  );

  const todayHours = useMemo(
    () => (practiceInfo?.hours ? getTodayOpeningHours(practiceInfo.hours) : []),
    [practiceInfo?.hours],
  );

  return (
    <>
      <StepContainer>
        <CardTitleWrapper
          borderColor={theme.palette.LUX.obTokens.bordersColorNeutralSecondary}
        >
          <LuxTypography variant="heading4">
            {t('practiceCard.directions')}
          </LuxTypography>
        </CardTitleWrapper>
        <Stack padding="0.5rem 1rem 1rem 1rem" flexDirection="row" gap="1rem">
          {!isMobile &&
            practiceConfig?.practiceInformation?.showPracticeHours && (
              <Stack flexDirection="column" gap="0.5rem">
                <LuxTypography fontWeightBold>
                  {t('practiceCard.openingHours')}
                </LuxTypography>
                <Stack
                  flexDirection="row"
                  gap={'0.25rem'}
                  alignContent="center"
                  width="100%"
                >
                  {isOpen ? (
                    <LuxSelectablePill
                      selectable={false}
                      color={'success'}
                      text={t('practiceCard.open')}
                    />
                  ) : todayHours.length === 0 ? (
                    <LuxTypography>{t('practiceCard.closed')}</LuxTypography>
                  ) : (
                    <LuxTypography>{todayHours.join(', ')}</LuxTypography>
                  )}
                  {isOpen && closingTime && (
                    <LuxTypography alignContent="center">
                      {'-'} {t('practiceCard.closeAt')} {closingTime}
                    </LuxTypography>
                  )}
                </Stack>
                <Stack flexDirection="column" gap="0.25rem" width="100%">
                  {daysOpeningHours.map(day => (
                    <LuxTypography
                      key={day.key}
                      display="block"
                      color={theme.palette.LUX.obTokens.textColorBaseSecondary}
                    >
                      {day.label}: {day.ranges}
                    </LuxTypography>
                  ))}
                </Stack>
              </Stack>
            )}
          <Stack
            flexDirection="column"
            gap="0.5rem"
            flex="1 1 60%"
            sx={{
              ...(!practiceConfig?.practiceInformation?.showPracticeHours && {
                minHeight: 300,
                flex: '1 1 100%',
              }),
            }}
          >
            <DirectionsCardMap isStatic />
            <Stack
              flexDirection={isMobile ? 'column' : 'row'}
              gap={isMobile ? 'unset' : '0.5rem'}
              alignItems={isMobile ? 'flex-start' : 'center'}
            >
              <LuxTypography
                variant="textMdRegular"
                color={theme.palette.LUX.obTokens.textColorBaseSecondary}
              >
                {formatGeneralAddress(practiceInfo)}
              </LuxTypography>
              <LuxButton
                variant="link"
                size="small"
                onClick={() => setOpenDirectionsModal(true)}
              >
                <LuxTypography variant="textMdRegular">
                  {t('generalCta.getDirections')}
                </LuxTypography>
              </LuxButton>
            </Stack>
          </Stack>
        </Stack>
      </StepContainer>
      <DirectionsModal
        open={openDirectionsModal}
        onClose={() => setOpenDirectionsModal(false)}
      />
    </>
  );
}
